import React from 'react'
import i18n from 'i18next'
import * as countries from 'i18n-iso-countries'
import { useMediaQuery, useTheme, Box } from '@mui/material'
import { IColumn } from 'packages/ui/Table'
import { createExpandedRowComponent, useResponsiveTableContext } from 'packages/ui/Table'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import CardActionsButtonsGroup from 'components/global/CardActions/CardActionsButtonsGroup'
import { getCivilityLabel, formatDateByLocale } from '../../helpers'

const ActionsWithExpand = ({ user, ctx }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const responsiveTableContext = useResponsiveTableContext()

  if (!isMobile || !responsiveTableContext) {
    return null
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      width: '100%'
    }}>
      <CardActionsButtonsGroup>
        <CardActionButton
          tooltiptitle={responsiveTableContext.isRowExpanded(user.id) ? ctx.t('statistics.customers.collapse_details') : ctx.t('statistics.customers.expand_details')}
          iconlabel={responsiveTableContext.isRowExpanded(user.id) ? 'chevron-up' : 'chevron-down'}
          onClick={(e) => {
            e.stopPropagation()
            responsiveTableContext.toggleRowExpansion(user.id)
          }}
        />
      </CardActionsButtonsGroup>
    </Box>
  )
}

interface ExpandedRowField {
  key: string
  label: string
  value: string | React.ReactNode
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ExpandedRowDetails = createExpandedRowComponent<any, { hasCivility?: boolean }>((user, t, additionalProps) => {
  const fields: ExpandedRowField[] = []
  
  if (additionalProps?.hasCivility && user.civility) {
    fields.push({
      key: 'civility',
      label: t('statistics.customers.table.civility'),
      value: getCivilityLabel(user.civility, t)
    })
  }
  
  fields.push(
    {
      key: 'account',
      label: t('statistics.customers.table.account'),
      value: user.account_parent?.name || '-'
    },
    {
      key: 'site',
      label: t('statistics.customers.table.site'),
      value: user.site?.name || '-'
    },
    {
      key: 'country',
      label: t('statistics.customers.table.country'),
      value: user.country ? countries.getName(user.country.toLowerCase(), i18n.language) || '-' : '-'
    },
    {
      key: 'created_at',
      label: t('statistics.customers.table.created_at'),
      value: formatDateByLocale(user.created_at as string, i18n.language)
    }
  )
  
  return fields
})

const COLUMNS_CONFIG: IColumn[] = [
  {
    name: 'civility',
    header: {
      label: (ctx) => ctx.t('statistics.customers.table.civility'),
    },
    value: (user, ctx) => getCivilityLabel(user.civility as string, ctx?.t),
    priority: 2
  },
  {
    name: 'first_name',
    header: {
      label: (ctx) => ctx.t('statistics.customers.table.first_name'),
    },
    value: (user) => user.first_name as string,
    priority: 1
  },
  {
    name: 'last_name',
    header: {
      label: (ctx) => ctx.t('statistics.customers.table.last_name'),
    },
    value: (user) => user.last_name as string,
    sortBy: 'last_name',
    priority: 1
  },
  {
    name: 'email',
    header: {
      label: (ctx) => ctx.t('statistics.customers.table.email'),
    },
    value: (user) => user.email as string,
    priority: 1
  },
  {
    name: 'account',
    header: {
      label: (ctx) => ctx.t('statistics.customers.table.account'),
    },
    value: (user) => {
      const value = user.account_parent as { name: string }
      return value?.name
    },
    priority: 3
  },
  {
    name: 'site',
    header: {
      label: (ctx) => ctx.t('statistics.customers.table.site'),
    },
    value: (user) => {
      const value = user.site as { name: string }
      return value.name
    },
    sortBy: 'site',
    sortable: true,
    priority: 2
  },
  {
    name: 'country',
    header: {
      label: (ctx) => ctx.t('statistics.customers.table.country'),
    },
    value: (user) => {
      if (!user.country) return '-'
      return countries.getName((user.country as string).toLowerCase(), i18n.language) || '-'
    },
    priority: 2
  },
  {
    name: 'created_at',
    header: {
      label: (ctx) => ctx.t('statistics.customers.table.created_at'),
    },
    value: (user) => formatDateByLocale(user.created_at as string, i18n.language),
    sortBy: 'created_at',
    sortable: true,
    priority: 3
  },
  {
    name: 'actions',
    header: {
      label: () => '',
    },
    value: (user, ctx) => <ActionsWithExpand user={user} ctx={ctx} />,
    style: { width: '60px', textAlign: 'right' },
    priority: 1
  }
]

export default COLUMNS_CONFIG
